import { toast } from 'components/ui/toast-store'
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Upload, FolderOpen } from 'lucide-react'
import { Button } from 'components/ui/button'
import { Spinner } from 'components/ui/spinner'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from 'components/ui/table'
import {
	clearError,
	initializeForScope,
} from 'slices/documentLibrarySlice'
import { initializeProjects } from 'slices/projectSlice'
import {
	DocumentLibraryList,
	DocumentRow,
	UploadFilesDialog,
	VersionHistoryDialog,
	DocumentPreviewDialog,
	DocumentShareDialog,
	DocumentEditSheet,
	getCurrentVersion,
	matchesScope,
	scopeKey,
	type DocumentLibraryState,
	type LibraryDocument,
	type LibraryScopeArgs,
} from 'features/DocumentLibrary'

/**
 * Portfolio Documents tab — backed by the scope-aware Document Library.
 *
 * Two sections:
 *  1. Portfolio-scope library (libraryDocument scope='portfolio'): full
 *     read/write with preview, version history, share, edit-sheet.
 *  2. Project Documents roll-up (libraryDocument scope='project' per
 *     project whose projectAsset is one of the portfolio's child
 *     locations): a per-project view of docs. Rows are read-only —
 *     click avatar/filename to preview, click download to fetch the
 *     original. For full management open the project's own Documents
 *     tab.
 */
type LocationLite = { _id: string; image?: string | null; assetName: string }

const docMostRecentTime = (doc: LibraryDocument): number => {
	const v = getCurrentVersion(doc)
	const candidate = v?.uploadedAt || doc.updatedAt
	if (!candidate) return 0
	const t = new Date(candidate).getTime()
	return Number.isFinite(t) ? t : 0
}

const PortfolioDocumentsCN: React.FC = () => {
	const { id } = useParams<{ id: string }>()
	const portfolioId = id || ''
	const dispatch = useDispatch()

	const scopeArgs: LibraryScopeArgs = { scope: 'portfolio', portfolioId }

	const portfolio = useSelector((s: any) => s.portfolios.entities[portfolioId])
	const sliceError = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			state.libraryDocuments.error,
	)

	// Notifications go through the shared toast store (MYD-561), titled as before.
	const showToast = ({ message }: { message: string }) =>
		toast.error(message, { title: 'Something went wrong' })

	useEffect(() => {
		if (sliceError) {
			showToast({ message: sliceError })
			dispatch(clearError())
		}
	}, [sliceError, dispatch])

	// Portfolio-scope docs — drives collision detection for the upload
	// dialog (new-version vs. fresh upload).
	const existingDocuments = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) => {
			const { entities, ids } = state.libraryDocuments
			return ids
				.map(_id => entities[_id])
				.filter(
					(d): d is LibraryDocument =>
						!!d && matchesScope(d, scopeArgs) && !d.isDeleted,
				)
				.map(d => ({ id: d._id, libraryName: d.libraryName }))
		},
	)

	const [uploadOpen, setUploadOpen] = useState(false)
	const [versionsDocId, setVersionsDocId] = useState<string | null>(null)
	const [previewDocId, setPreviewDocId] = useState<string | null>(null)
	const [shareTarget, setShareTarget] = useState<
		{ documentId: string; versionId?: string } | null
	>(null)
	const [editDocId, setEditDocId] = useState<string | null>(null)

	const openUpload = useCallback(() => setUploadOpen(true), [])

	const handleShare = useCallback(
		(documentId: string, versionId?: string) => {
			setShareTarget({ documentId, versionId })
		},
		[],
	)

	const handleEditUploadNewVersion = useCallback(() => {
		setEditDocId(null)
		setUploadOpen(true)
	}, [])

	const headerSubtitle = useMemo(
		() =>
			'Upload, version, preview, and share documents for this portfolio. Each upload is stored with its original filename and full version history.',
		[],
	)

	// --- Location Documents roll-up ---
	const locationsList: LocationLite[] = useMemo(() => {
		const raw = portfolio?.locations || []
		return raw
			.map((entry: any) => entry?.location)
			.filter(Boolean)
			.map((loc: any) => ({
				_id: loc._id,
				image: loc.image,
				assetName: loc.assetName || '',
			}))
	}, [portfolio?.locations])

	// Stable key for the locationsList — drives the fetch effect. Avoids
	// re-fetching when the array reference changes but contents don't.
	const locationsKey = useMemo(
		() => locationsList.map(l => l._id).join(','),
		[locationsList],
	)

	// Track which scopes have initialized — for showing the loading
	// spinner while at least one project-scope fetch is in flight.
	const initializedScopes = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			state.libraryDocuments.initializedScopes,
	)

	const sliceEntities = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			state.libraryDocuments.entities,
	)
	const sliceIds = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			state.libraryDocuments.ids,
	)

	// --- Project Documents roll-up ---
	// For each child location, load its projects. initializeProjects
	// resets state.projects.ids per call but accumulates entities (Immer
	// per-key mutation), so we filter via entities + projectAsset.
	useEffect(() => {
		if (!locationsList.length) return
		for (const loc of locationsList) {
			dispatch((initializeProjects as any)({ locationId: loc._id }))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locationsKey, dispatch])

	// Filter the global projects entities to those whose projectAsset
	// is one of the portfolio's child locations.
	const portfolioProjects = useSelector((state: any) => {
		const locIds = new Set(locationsList.map(l => l._id))
		if (!locIds.size) return [] as any[]
		const entities = state.projects?.entities || {}
		const out: any[] = []
		for (const projId of Object.keys(entities)) {
			const p = entities[projId]
			if (!p || p.isDeleted) continue
			const assetId =
				typeof p.projectAsset === 'string'
					? p.projectAsset
					: p.projectAsset?._id
			if (assetId && locIds.has(assetId)) out.push(p)
		}
		return out
	})

	// Stable key for the project-fetch effect.
	const projectsKey = useMemo(
		() =>
			portfolioProjects
				.map((p: any) => p._id)
				.sort()
				.join(','),
		[portfolioProjects],
	)

	// Fetch project-scope docs for each project in the portfolio.
	useEffect(() => {
		if (!portfolioProjects.length) return
		for (const proj of portfolioProjects) {
			dispatch(
				(initializeForScope as any)({
					scope: 'project',
					projectId: proj._id,
				}),
			)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectsKey, dispatch])

	const projectFilesLoading = useMemo(() => {
		if (!portfolioProjects.length) return false
		return portfolioProjects.some(
			(p: any) =>
				!initializedScopes.includes(
					scopeKey({ scope: 'project', projectId: p._id }),
				),
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectsKey, initializedScopes])

	// Map of locationId → { assetName, image } for the project card header.
	const locationMetaById = useMemo(() => {
		const m = new Map<string, { assetName: string; image?: string | null }>()
		for (const loc of locationsList)
			m.set(loc._id, { assetName: loc.assetName, image: loc.image })
		return m
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locationsKey])

	const groupedProjectRollup = useMemo(() => {
		const byProject = new Map<string, LibraryDocument[]>()
		const projectIds = new Set(portfolioProjects.map((p: any) => p._id))
		for (const projId of projectIds)
			byProject.set(projId as string, [])
		for (const docId of sliceIds) {
			const doc = sliceEntities[docId]
			if (!doc || doc.isDeleted) continue
			if ((doc.scope || 'project') !== 'project') continue
			const projId =
				typeof doc.project === 'string'
					? doc.project
					: (doc.project as any)?._id
			if (!projId || !projectIds.has(projId)) continue
			const bucket = byProject.get(projId)
			if (bucket) bucket.push(doc)
		}
		const groups: {
			project: any
			locationName: string
			locationImage?: string | null
			docs: LibraryDocument[]
		}[] = []
		for (const proj of portfolioProjects) {
			const docs = (byProject.get(proj._id) || []).slice()
			docs.sort((a, b) => docMostRecentTime(b) - docMostRecentTime(a))
			if (!docs.length) continue
			const assetId =
				typeof proj.projectAsset === 'string'
					? proj.projectAsset
					: proj.projectAsset?._id
			const meta = locationMetaById.get(assetId)
			groups.push({
				project: proj,
				locationName: meta?.assetName || '',
				locationImage: meta?.image ?? null,
				docs,
			})
		}
		groups.sort((a, b) => {
			const aTime = a.docs[0] ? docMostRecentTime(a.docs[0]) : 0
			const bTime = b.docs[0] ? docMostRecentTime(b.docs[0]) : 0
			return bTime - aTime
		})
		return groups
	}, [sliceEntities, sliceIds, portfolioProjects, locationMetaById])

	if (!portfolio) return null

	return (
		<div className="space-y-8">
			{/* Portfolio-scope library — new R2-backed Document Library. */}
			<section>
				<div className="flex flex-wrap justify-between items-center gap-4 mb-6">
					<div className="flex flex-col gap-1">
						<h2 className="text-2xl font-bold m-0">Documents</h2>
						<span className="text-sm text-gray-600">{headerSubtitle}</span>
					</div>
					<Button onClick={openUpload}>
						<Upload className="w-4 h-4 mr-1" />
						<span>Upload Document</span>
					</Button>
				</div>

				<DocumentLibraryList
					{...scopeArgs}
					onPreview={setPreviewDocId}
					onShowVersions={setVersionsDocId}
					onShare={handleShare}
					onRowClick={setEditDocId}
					onUpload={openUpload}
					hideWhenEmpty={groupedProjectRollup.length > 0}
				/>
			</section>

			{/* Project Documents roll-up — read-only, sourced from
			    libraryDocument scope='project' rows for each project whose
			    projectAsset is one of this portfolio's child locations.
			    For full management open the project's own Documents tab. */}
			<section className="space-y-3">
				<div className="flex items-center gap-2 pt-4 border-t border-gray-200">
					<FolderOpen className="w-4 h-4 text-gray-500" />
					<h3 className="text-base font-semibold text-gray-700 m-0">
						Project Documents
					</h3>
					{projectFilesLoading && <Spinner containerHeight="auto" />}
				</div>

				{!projectFilesLoading && groupedProjectRollup.length === 0 && (
					<p className="text-sm text-gray-500 italic flex items-center gap-2">
						<FolderOpen className="w-4 h-4" />
						No documents have been added to any project in this portfolio.
					</p>
				)}

				{groupedProjectRollup.length > 0 && (
					<div className="rounded-lg border border-gray-200">
						<Table className="table-fixed w-full">
							<TableHeader>
								<TableRow className="border-b-2 border-[#0080fe] hover:bg-transparent">
									<TableHead className="text-gray-700 text-xs font-semibold whitespace-nowrap w-14"></TableHead>
									<TableHead className="text-gray-700 text-xs font-semibold whitespace-nowrap">
										File Name
									</TableHead>
									<TableHead className="text-gray-700 text-xs font-semibold whitespace-nowrap w-40">
										Owner
									</TableHead>
									<TableHead className="text-gray-700 text-xs font-semibold whitespace-nowrap w-24">
										Uploaded
									</TableHead>
									<TableHead className="text-gray-700 text-xs font-semibold whitespace-nowrap w-24">
										Doc Date
									</TableHead>
									<TableHead className="text-gray-700 text-xs font-semibold whitespace-nowrap w-20 text-center">
										Version
									</TableHead>
									<TableHead className="text-gray-700 text-xs font-semibold whitespace-nowrap w-16 text-center">
										Share
									</TableHead>
									<TableHead className="text-gray-700 text-xs font-semibold whitespace-nowrap w-20 text-center">
										Download
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{groupedProjectRollup.map(
									({ project, locationName, locationImage, docs }) => (
										<Fragment key={project._id}>
											{/* Project group separator row — spans every data
											    column. Avatar is the parent location image so
											    the project's deal location is visible at a glance. */}
											<TableRow className="bg-gray-50 hover:bg-gray-50 border-t border-gray-200">
												<TableCell colSpan={8} className="py-2">
													<div className="flex items-center gap-3">
														<Avatar className="h-8 w-8 flex-shrink-0">
															<AvatarImage
																src={locationImage ?? undefined}
																alt={locationName || project.projectName}
															/>
															<AvatarFallback className="text-xs">
																{project.projectName?.charAt(0)?.toUpperCase() ||
																	'P'}
															</AvatarFallback>
														</Avatar>
														<div className="flex flex-col min-w-0">
															<span className="text-sm font-semibold text-gray-900 truncate">
																{project.projectName}
															</span>
															{locationName && (
																<span className="text-[11px] text-gray-500 truncate">
																	{locationName}
																</span>
															)}
														</div>
														<span className="ml-auto inline-flex items-center justify-center text-[10px] text-gray-600 bg-gray-100 rounded-full px-2 py-0.5 flex-shrink-0">
															{docs.length}
														</span>
													</div>
												</TableCell>
											</TableRow>
											{docs.map(doc => (
												<DocumentRow
													key={doc._id}
													document={doc}
													onPreview={setPreviewDocId}
													onShowVersions={setVersionsDocId}
													onShare={handleShare}
													onRowClick={setEditDocId}
												/>
											))}
										</Fragment>
									),
								)}
							</TableBody>
						</Table>
					</div>
				)}
			</section>

			<UploadFilesDialog
				open={uploadOpen}
				onClose={() => setUploadOpen(false)}
				scopeArgs={scopeArgs}
				existingDocuments={existingDocuments}
			/>

			<VersionHistoryDialog
				open={!!versionsDocId}
				onClose={() => setVersionsDocId(null)}
				documentId={versionsDocId}
				onShareVersion={handleShare}
			/>

			<DocumentPreviewDialog
				open={!!previewDocId}
				onClose={() => setPreviewDocId(null)}
				documentId={previewDocId}
			/>

			<DocumentShareDialog
				open={!!shareTarget}
				onClose={() => setShareTarget(null)}
				documentId={shareTarget?.documentId ?? null}
				versionId={shareTarget?.versionId}
			/>

			<DocumentEditSheet
				open={!!editDocId}
				onClose={() => setEditDocId(null)}
				documentId={editDocId}
				onUploadNewVersion={handleEditUploadNewVersion}
				onShare={handleShare}
				onShowVersions={setVersionsDocId}
			/>

		</div>
	)
}

export default PortfolioDocumentsCN
